import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getCurrentUser, signOut, signInWithRedirect, signIn, confirmSignIn } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { githubConfig } from '../config/auth';
import { getSession } from '../utils/session';

const USER_CACHE_KEY = 'commit-you-user';

interface User {
  id: string;
  email: string;
  username: string;
  name?: string;
  picture?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => Promise<void>;
  signInWithGitHub: () => void;
  handleGitHubCallback: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  updateUserProfile: (updates: { displayName?: string; avatarUrl?: string; username?: string }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get cached user from localStorage (runs synchronously on init)
function getCachedUser(): User | null {
  try {
    const cached = localStorage.getItem(USER_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

// Cache user to localStorage
function cacheUser(user: User | null) {
  try {
    if (user) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
}

// Singleton promise for auth check to prevent duplicate Cognito calls
let authCheckPromise: Promise<User | null> | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize from cache for instant UI (no flash)
  const [user, setUser] = useState<User | null>(getCachedUser);
  const [isLoading, setIsLoading] = useState(!getCachedUser());

  async function checkUser(force = false): Promise<User | null> {
    // If already checking and not forced, reuse existing promise
    if (authCheckPromise && !force) {
      return authCheckPromise;
    }

    authCheckPromise = (async () => {
      try {
        const currentUser = await getCurrentUser();
        // Use shared session singleton to avoid duplicate Cognito calls
        const session = await getSession();
        const idToken = session.tokens?.idToken;

        if (idToken) {
          const payload = idToken.payload;
          const userId = (payload['custom:userId'] as string) || currentUser.userId;
          const newUser: User = {
            // Use custom claims for account linking (falls back to token defaults)
            id: userId,
            email: payload.email as string,
            username: (payload['custom:username'] as string) || '',
            name: (payload['custom:displayName'] as string) || payload.name as string | undefined,
            picture: (payload['custom:avatarUrl'] as string) || payload.picture as string | undefined,
          };
          setUser(newUser);
          cacheUser(newUser);
          return newUser;
        }
        return null;
      } catch {
        setUser(null);
        cacheUser(null);
        return null;
      } finally {
        setIsLoading(false);
        authCheckPromise = null;
      }
    })();

    return authCheckPromise;
  }

  useEffect(() => {
    checkUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkUser(true); // Force fresh check on sign in
          break;
        case 'signedOut':
          setUser(null);
          cacheUser(null);
          break;
      }
    });

    return unsubscribe;
  }, []);

  async function handleSignIn() {
    await signInWithRedirect({ provider: 'Google' });
  }

  function handleSignInWithGitHub() {
    // Generate state for CSRF protection
    const state = crypto.randomUUID();
    sessionStorage.setItem('github_oauth_state', state);

    // Build GitHub OAuth URL
    const params = new URLSearchParams({
      client_id: githubConfig.clientId,
      redirect_uri: githubConfig.redirectUri,
      scope: githubConfig.scope,
      state,
    });

    // Redirect to GitHub
    window.location.href = `https://github.com/login/oauth/authorize?${params}`;
  }

  async function handleGitHubCallback(code: string): Promise<void> {
    setIsLoading(true);

    try {
      // 1. Exchange code for GitHub access token via backend
      const tokenRes = await fetch(githubConfig.tokenExchangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          redirect_uri: githubConfig.redirectUri,
        }),
      });

      const tokenData = await tokenRes.json();
      if (!tokenData.access_token) {
        throw new Error(tokenData.error || 'Failed to get GitHub access token');
      }

      // 2. Get user info and email from GitHub (in parallel)
      const [userRes, emailsRes] = await Promise.all([
        fetch('https://api.github.com/user', {
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
            Accept: 'application/vnd.github.v3+json',
          },
        }),
        fetch('https://api.github.com/user/emails', {
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
            Accept: 'application/vnd.github.v3+json',
          },
        }),
      ]);

      const [githubUser, emails] = await Promise.all([userRes.json(), emailsRes.json()]);
      const primaryEmail = emails.find((e: { primary: boolean; verified: boolean; email: string }) => e.primary && e.verified)?.email;

      if (!primaryEmail) {
        throw new Error('No verified email found on GitHub account');
      }

      // Store GitHub profile URL for auto-fill in SettingsModal
      if (githubUser.html_url) {
        localStorage.setItem('github_profile_url', githubUser.html_url);
      }

      // 3. Initiate Cognito custom auth flow
      const signInResult = await signIn({
        username: primaryEmail,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
      });

      // 4. Respond to custom challenge with GitHub token
      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        const confirmResult = await confirmSignIn({
          challengeResponse: tokenData.access_token,
        });

        if (confirmResult.isSignedIn) {
          await checkUser();
        } else {
          throw new Error('GitHub authentication failed');
        }
      } else {
        throw new Error('Unexpected auth flow step');
      }
    } catch (error) {
      console.error('GitHub auth error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    setUser(null);
    cacheUser(null);
  }

  async function getAccessToken(): Promise<string | null> {
    try {
      const session = await getSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  function updateUserProfile(updates: { displayName?: string; avatarUrl?: string; username?: string }) {
    if (user) {
      const updatedUser = {
        ...user,
        name: updates.displayName !== undefined ? updates.displayName : user.name,
        picture: updates.avatarUrl !== undefined ? updates.avatarUrl : user.picture,
        username: updates.username !== undefined ? updates.username : user.username,
      };
      setUser(updatedUser);
      cacheUser(updatedUser);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        signIn: handleSignIn,
        signInWithGitHub: handleSignInWithGitHub,
        handleGitHubCallback,
        signOut: handleSignOut,
        getAccessToken,
        updateUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
